/** A batch on a shift row: the product once, its overtime, open seats and its people. */
import React, { useCallback, useMemo } from "react";

import { ManualMoves } from "../../controller/use-manual-moves";
import { CrewItem, fmtOvertime, isoAt, partHours } from "../../utils/crew-board"; // eslint-disable-line sort-imports
import { bestPhaseTarget, DropState, phaseDropState } from "../../utils/drop-state"; // eslint-disable-line sort-imports
import { OutcomeMark, OutcomeTone } from "../../utils/job-outcome";
import { jobLabel } from "../../utils/lane-rows";
import { lbb } from "../../utils/tokens";
import { MoveTarget } from "../../utils/undo";
import { FillRequest } from "../fill-picker";
import { dropSurface, useCardDrop, WorkerDragItem } from "../manual-controls"; // eslint-disable-line sort-imports
import { PersonChip } from "./person-chip";

const TONE: Record<OutcomeTone, string> = { success: lbb.success, warning: lbb.warning, danger: lbb.brick, info: lbb.navy };

// ---------------------------------------------------------------- a batch

export const BatchCard: React.FC<{
	item: CrewItem;
	weekStart: Date;
	workstation: string;
	moves: ManualMoves;
	selected: number | null;
	onSelect: (id: number | null) => void;
	onFill: (request: FillRequest) => void;
	pinEndFor: (workerId: string, day: string) => string | null;
	/** A click on the card (not picking anyone) opens its people to edit. */
	onEdit: (item: CrewItem, workstation: string) => void;
	/** What happened to the order (recorded after its shift), if anything. */
	mark?: OutcomeMark | null;
	/** A late order: move its line on. */
	onUpdateSchedule?: (workOrderId: number) => void;
	/** A person picked in the people list: a click on the batch places them. */
	picked?: WorkerDragItem | null;
	onPersonPlaced?: () => void;
}> = ({ item, weekStart, workstation, moves, selected, onSelect, onFill, pinEndFor, onEdit, mark, onUpdateSchedule, picked = null, onPersonPlaced }) => {
	const { step, group } = item;
	const droppable = Boolean(group && group.dropPhaseId !== null);
	const target = useMemo<MoveTarget>(
		() => ({ kind: "phase", workOrderId: step.workOrderId, phaseId: group?.dropPhaseId ?? -1, label: `${step.title} · ${group?.operationName ?? ""}` }),
		[step.workOrderId, step.title, group]
	);
	const { targetIndex } = moves;
	const grade = useMemo(
		() => (droppable && group ? bestPhaseTarget(group.phaseIds.map((id) => targetIndex.phase(step.workOrderId, id))) : undefined),
		[droppable, group, targetIndex, step.workOrderId]
	);
	const state = useMemo<DropState>(
		() => (droppable ? phaseDropState(grade, targetIndex.loaded) : { ...phaseDropState(undefined), allowed: false }),
		[droppable, grade, targetIndex.loaded]
	);
	const startIso = isoAt(weekStart, item.start);
	const endIso = isoAt(weekStart, item.end);
	const handlePlace = useCallback(
		(worker: WorkerDragItem) => {
			if (!group) return;
			void moves.placeWorker({
				workerId: worker.workerId,
				workerName: worker.workerName,
				workOrderId: step.workOrderId,
				phaseId: group.dropPhaseId,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				start: startIso,
				end: endIso,
				workstation,
				stepTitle: step.title,
			});
		},
		[group, moves, step.workOrderId, step.title, startIso, endIso, workstation]
	);
	const { isOver, canDrop, chipOver, dropRef } = useCardDrop(target, state, moves, false, droppable ? handlePlace : undefined);
	const receivingPerson = picked !== null && droppable && !item.away.concat(item.crew).some((c) => c.workerId === picked.workerId);
	const receiving = (selected !== null && droppable && !item.away.concat(item.crew).some((c) => c.id === selected)) || receivingPerson;
	const handoff = useCallback(
		(event: React.MouseEvent) => {
			if (receivingPerson) {
				event.stopPropagation();
				if (picked) handlePlace(picked);
				onPersonPlaced?.();
				return;
			}
			if (receiving && state.allowed && group) {
				event.stopPropagation();
				moves.requestMove(selected as number, target, {
					wholeStep: false,
					confirmFirst: state.confirm ? `${state.caption || "Not qualified"} for ${group.jobRole}. Place anyway?` : null,
				});
				onSelect(null);
				return;
			}
			if (selected === null && picked === null) onEdit(item, workstation);
		},
		[receivingPerson, picked, handlePlace, onPersonPlaced, receiving, state.allowed, state.confirm, state.caption, group, moves, selected, target, onSelect, onEdit, item, workstation]
	);
	const fillOpen = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			if (!group) return;
			const slot = group.slots.find((s) => s.startIso < endIso && s.endIso > startIso);
			onFill({
				workOrderId: step.workOrderId,
				phaseId: slot?.phaseId ?? group.dropPhaseId,
				start: slot?.startIso ?? startIso,
				end: slot?.endIso ?? endIso,
				jobRoleId: group.jobRoleId,
				jobRoleName: group.jobRole,
				title: `${step.title} · ${group.operationName} · ${group.jobRole}`,
			});
		},
		[group, onFill, step.workOrderId, step.title, startIso, endIso]
	);
	const updateSchedule = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			onUpdateSchedule?.(step.workOrderId);
		},
		[onUpdateSchedule, step.workOrderId]
	);
	const markColor = mark ? TONE[mark.tone] : null;
	const dragging = Boolean(moves.dragging);
	const details = step.details.map((d) => `${d.label}: ${d.value}`).join("\n");
	const label = group?.operationName ?? step.title;
	return (
		<div
			ref={dropRef}
			onClick={handoff}
			title={[step.title, step.subtitle, `This shift: ${partHours(item.start, item.end)}`, details].filter(Boolean).join("\n")}
			style={{
				position: "relative",
				border: `1px solid ${item.offShift ? lbb.warning : lbb.navy20}`,
				// A recorded outcome colours the edge; otherwise overtime / no shift, then the plain line.
				borderLeft: `3px solid ${markColor ?? (item.offShift || item.overtimeHours > 0 ? lbb.warning : lbb.navy)}`,
				borderRadius: 4,
				background: receiving || chipOver ? lbb.dropTint : lbb.paper,
				padding: "6px 7px",
				display: "flex",
				flexDirection: "column",
				gap: 4,
				cursor: receiving ? "copy" : "pointer",
				...dropSurface(state, (dragging || receiving) && droppable, isOver, canDrop),
				...(chipOver ? { outline: `2px solid ${lbb.navy}`, outlineOffset: -2 } : {}),
			}}
		>
			{/* The full product name and batch number; its details and this shift's hours are on hover. */}
			<div style={{ display: "flex", alignItems: "baseline", gap: 4, minWidth: 0 }}>
				{item.continuesFrom && <span title="Started on an earlier shift" style={{ color: lbb.navy40, fontSize: 11 }}>◂</span>}
				<span style={{ fontFamily: lbb.fontBrand, fontSize: 13, fontWeight: 700, lineHeight: 1.25, color: lbb.navy, overflowWrap: "anywhere", minWidth: 0 }}>
					{jobLabel(step)}
				</span>
				{item.continuesTo && <span title="Goes on into the next shift" style={{ color: lbb.navy40, fontSize: 11 }}>▸</span>}
			</div>
			{/* The plan's own label for the batch ("Mixed Salts Drug Seal Coating #1"), when it says something the headline does not. */}
			{step.subtitle && step.subtitle !== jobLabel(step) && (
				<div style={{ fontFamily: lbb.fontUi, fontSize: 11, color: lbb.ink60, marginTop: -2, overflowWrap: "anywhere" }}>{step.subtitle}</div>
			)}
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
				{mark && (
					<span style={{ fontFamily: lbb.fontUi, fontSize: 10.5, fontWeight: 700, color: markColor ?? lbb.navy, border: `1px solid ${markColor ?? lbb.navy}`, borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" }}>
						{mark.text}
					</span>
				)}
				{mark?.needsUpdate && onUpdateSchedule && (
					<span
						role="button"
						onClick={updateSchedule}
						title="Move this line's later batches on"
						style={{ fontFamily: lbb.fontUi, fontSize: 10.5, fontWeight: 700, color: lbb.paper, background: lbb.brick, borderRadius: 3, padding: "0 6px", cursor: "pointer", whiteSpace: "nowrap" }}
					>
						Update schedule
					</span>
				)}
				{item.overtimeHours > 0 && (
					<span
						title={`Runs ${item.overtimeHours} h past the line's last shift — finished on overtime`}
						style={{ fontFamily: lbb.fontUi, fontSize: 10.5, fontWeight: 700, color: lbb.warning, border: `1px solid ${lbb.warning}`, borderRadius: 3, padding: "0 5px", whiteSpace: "nowrap" }}
					>
						{fmtOvertime(item.overtimeHours)}
					</span>
				)}
				{item.offShift && (
					<span style={{ fontFamily: lbb.fontUi, fontSize: 10.5, fontWeight: 700, color: lbb.warning, whiteSpace: "nowrap" }}>no shift</span>
				)}
				{item.open > 0 && (
					<span
						role="button"
						onClick={fillOpen}
						title="Fill from the bench"
						style={{ fontFamily: lbb.fontUi, fontSize: 10.5, fontWeight: 700, color: lbb.brick, border: `1px dashed ${lbb.brick}`, borderRadius: 3, padding: "0 5px", cursor: "pointer", whiteSpace: "nowrap" }}
					>
						{item.open} open +
					</span>
				)}
			</div>
			{(item.away.length > 0 || item.crew.length > 0) && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
					{item.away.map((card) => (
						<PersonChip key={card.id} card={card} away label={label} moves={moves} selected={selected} onSelect={onSelect} pinnedUntil={pinEndFor(card.workerId, card.dayIso)} />
					))}
					{item.crew.map((card) => (
						<PersonChip key={card.id} card={card} away={false} label={label} moves={moves} selected={selected} onSelect={onSelect} pinnedUntil={pinEndFor(card.workerId, card.dayIso)} />
					))}
				</div>
			)}
			{(dragging || receiving) && droppable && state.caption && (
				<span style={{ fontFamily: lbb.fontUi, fontSize: 10, color: state.outline }}>{state.caption}</span>
			)}
		</div>
	);
};
